import { Icon } from '@iconify/react';
import { Alert, IconButton, Link, Stack, Tooltip } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { usePasswordSchemas } from 'hooks/useYupSchemas';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { PATH_AUTH } from '../../../routes/paths';
import { NavLink as RouterLink } from 'react-router';
import useDisplayMessage from 'components/messages/useMessage';
import { Button, TextField } from '@nps.today/component-library';
import { getOrSetUpAppInsights } from 'appInsights';
import { getUserId } from 'utils/jwt';
import { useTranslator } from 'translation/useTranslator';

const componentDataTestId = 'login-form';

type InitialValues = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
  multiFactorCode?: string;
  isMultiFactorAuthEnabled: boolean;
};

export default function LoginForm(props: any) {
  const translator = useTranslator();
  const { login, updateMultiFactorEnabled } = useAuth();
  const { LoginSchema } = usePasswordSchemas();
  const isMountedRef = useIsMountedRef();
  const displayMessage = useDisplayMessage();
  const [isMultiFactorAuthEnabled, setIsMultiFactorAuthEnabled] = useState(false);

  const formik = useFormik<InitialValues>({
    initialValues: {
      email: '',
      password: '',
      remember: true,
      isMultiFactorAuthEnabled: false,
      multiFactorCode: ''
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, setFieldValue }) => {
      try {
        let organisations = await login(values.email, values.password, values.multiFactorCode);
        const appInsights = getOrSetUpAppInsights();
        const userId = getUserId();

        if (userId) {
          appInsights?.setAuthenticatedUserContext(userId);
        }

        if (organisations?.length >= 1) {
          // User has multiple orgs — show tenant picker
          props.setUserOrganisations(organisations);
        }
      } catch (error: any) {
        if (error.status === 'MfaCodeRequired') {
          setIsMultiFactorAuthEnabled(true);
          setFieldValue('isMultiFactorAuthEnabled', true);
        } else if (error.status === 'MfaSetupRequired') {
          updateMultiFactorEnabled(true, '');
        } else {
          displayMessage.error({ message: { title: translator.couldNotLogin() } });
        }

        if (isMountedRef.current) {
          setSubmitting(false);
          setErrors({ afterSubmit: error.message });
        }
      }
    }
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const onSendAuthCodeAgain = () => {
    login(values.email, values.password);
  };

  return (
    <div>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate autoSave="off" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

            <TextField
              dataTestId={`${componentDataTestId}-email`}
              fullWidth
              autoComplete="username"
              type="email"
              label={translator.emailAddress()}
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />

            <TextField
              dataTestId={`${componentDataTestId}-password`}
              enableShowHide
              fullWidth
              autoComplete="current-password"
              label={translator.password()}
              {...getFieldProps('password')}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />

            {isMultiFactorAuthEnabled && (
              <TextField
                dataTestId={`${componentDataTestId}-two-factor-code`}
                autoFocus
                fullWidth
                enableShowHide
                label={translator.multiFactorAuthentication()}
                {...getFieldProps('multiFactorCode')}
                error={Boolean(touched.multiFactorCode && errors.multiFactorCode)}
                helperText={touched.multiFactorCode && errors.multiFactorCode}
                endAdornments={[
                  <Tooltip key="resendCode" title={translator.sendCodeAgain()}>
                    <IconButton onClick={onSendAuthCodeAgain} edge="end">
                      <Icon icon="dashicons:update" />
                    </IconButton>
                  </Tooltip>
                ]}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ my: 2 }}>
            <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.forgotPassword}>
              {translator.forgotPasswordQuestionMark()}
            </Link>
          </Stack>
          <Button
            dataTestId="login"
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {translator.signIn()}
          </Button>
        </Form>
      </FormikProvider>
    </div>
  );
}
